/**
 * Locale-agnostic 번역 유틸리티.
 *
 * 설계 원칙:
 *   - **단일 책임**: key → text lookup + `{placeholder}` 치환만. 데이터 로딩/locale 결정은 호출자에게 위임.
 *   - **여러 locale을 한 번에 보유**: `setLocale()`로 즉시 swap 가능. 페이지 reload 없이 UI 갱신 가능.
 *   - **유연한 fallback**: 활성 locale에 키가 없으면 fallback locale에서 찾는다. 둘 다 없으면 키 문자열 그대로 반환 + onMissingKey.
 *   - **외부 의존 없음**: Locale 타입은 generic string. 호출자(앱)가 자체 union을 그대로 주입. SDK/프레임워크 0.
 *
 * 사용 패턴:
 * ```ts
 * const t = new Translator({
 *   dictionaries: { ko: { "hud.next": "다음" }, en: { "hud.next": "Next" } },
 *   locale: "ko",
 *   fallbackLocale: "ko",
 * });
 * t.t("hud.next");                 // "다음"
 * t.setLocale("en");
 * t.t("hud.next");                 // "Next"
 * t.t("shop.cost", { cost: 5 });   // 키 안에 {cost} placeholder가 있으면 치환
 * ```
 */

import type {
  TranslationDictionary,
  TranslationOptions,
  TranslationParams,
} from "./types.js";

export class Translator {
  private dictionaries: Record<string, TranslationDictionary>;
  private locale: string;
  private readonly fallbackLocale: string;
  private readonly onMissingKey?: (key: string, locale: string) => void;

  constructor(options: TranslationOptions) {
    this.dictionaries = { ...options.dictionaries };
    this.locale = options.locale;
    this.fallbackLocale = options.fallbackLocale ?? options.locale;
    this.onMissingKey = options.onMissingKey;
  }

  /** 활성 locale 반환. */
  getLocale(): string {
    return this.locale;
  }

  /**
   * 활성 locale 변경. 호출 후 다음 `t()`부터 새 locale에서 lookup.
   * 등록되지 않은 locale로 변경해도 throw하지 않는다 — fallbackLocale로 동작.
   */
  setLocale(locale: string): void {
    this.locale = locale;
  }

  /** 새 dictionary 묶음을 통째로 교체. dev에서 데이터 hot reload용. */
  replaceDictionaries(dictionaries: Record<string, TranslationDictionary>): void {
    this.dictionaries = { ...dictionaries };
  }

  /**
   * key → text 번역. 활성 locale → fallback locale 순으로 lookup.
   * 둘 다 miss면 onMissingKey 콜백 호출(있으면) + key 그대로 반환.
   * params가 있으면 `{name}` 토큰을 치환. undefined/null 값은 빈 문자열로.
   */
  t(key: string, params?: TranslationParams): string {
    const text = this.lookup(key);
    if (text === undefined) {
      if (this.onMissingKey !== undefined) {
        this.onMissingKey(key, this.locale);
      }
      return key;
    }
    if (params === undefined) {
      return text;
    }
    return this.interpolate(text, params);
  }

  /**
   * `t()`와 동일하지만 lookup 실패 시 undefined 반환 — 호출자가 fallback 분기를 명시적으로 처리할 때.
   */
  tOptional(key: string, params?: TranslationParams): string | undefined {
    const text = this.lookup(key);
    if (text === undefined) return undefined;
    if (params === undefined) return text;
    return this.interpolate(text, params);
  }

  /** 등록된 locale 목록. */
  getAvailableLocales(): ReadonlyArray<string> {
    return Object.keys(this.dictionaries);
  }

  /** 특정 locale에서 키 존재 여부 — 디버깅/테스트용. */
  hasKey(locale: string, key: string): boolean {
    const dict = this.dictionaries[locale];
    return dict !== undefined && Object.prototype.hasOwnProperty.call(dict, key);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 내부

  private lookup(key: string): string | undefined {
    const primary = this.dictionaries[this.locale]?.[key];
    if (primary !== undefined) return primary;
    if (this.locale !== this.fallbackLocale) {
      return this.dictionaries[this.fallbackLocale]?.[key];
    }
    return undefined;
  }

  private interpolate(text: string, params: TranslationParams): string {
    return text.replace(/\{(\w+)\}/g, (_match, name: string) => {
      const value = params[name];
      if (value === undefined || value === null) return "";
      return String(value);
    });
  }
}
